import React from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import Icon from '../../../shared/_TBComponents/Icon';
import Text from '../../../shared/_TBComponents/Text';

const PageWrapper = styled.div`
  height: 100%;
  overflow-y: auto;
  background: #fcfcfc;
  box-sizing: border-box;
`;

const HeaderContainer = styled.div`
  padding: 16px;
  border-bottom: 1px solid #D6D6D6;
`;

const ContentWrapper = styled.div`
  padding: 16px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ActionButton = styled.button`
  color: #1A1A1A;
  font-weight: 700;
  font-size: 14px;
  background: #ffffff;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  padding: 6px 16px;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  white-space: nowrap;
  width: 160px;
  text-align: center;
  flex-shrink: 0;
  text-decoration: none;
  display: inline-block;
  box-sizing: border-box;
  transition: background 120ms ease;

  &:hover {
    background: ${({ clickable }) => (clickable ? '#f5f5f5' : '#ffffff')};
  }
`;

const MobileBackLink = styled.button`
  background: none;
  border: none;
  padding: 16px 16px 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ManageHub = ({ isMobile }) => {
  const history = useHistory();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const userSettings = currentUser?.userSettings;
  const manageUsers = !!userSettings?.manageUsers;
  const exercises = !!userSettings?.exercises;
  const primaryAdmin = (currentUser?.primaryAdmin ?? 0) > 0;
  const hasCalendarAccess = currentUser?.calAccess === null
    || (currentUser?.calAccess?.length ?? 0) > 0;
  const hasGroupAccess = currentUser?.userGroupIds === null
    || (currentUser?.userGroupIds?.length ?? 0) > 0;

  const linkRow = (path, historyState) => ({
    href: path,
    onClick: (e) => {
      const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
      if (!isModified) {
        e.preventDefault();
        history.push(path, historyState);
      }
    },
  });

  const SECTIONS = [
    {
      title: 'Athletes',
      rows: [
        {
          label: 'Manage Athletes',
          description: 'Add, invite, or bulk upload athletes.',
          ...linkRow('/dashboard/manage-athletes'),
          visible: manageUsers,
        },
        {
          label: 'Easy Join Codes',
          description: 'Generate a unique code and a password allowing athletes to create their own account.',
          ...linkRow('/easy-join', { backPath: '/dashboard/manage', backLabel: 'Back to Manage' }),
          visible: manageUsers,
        },
      ],
    },
    {
      title: 'Calendars',
      rows: [
        {
          label: 'Manage Calendars',
          description: 'Build and schedule workouts to be completed on a specific date.',
          ...linkRow('/dashboard/manage-calendars'),
          visible: hasCalendarAccess,
        },
      ],
    },
    {
      title: 'Coaches',
      rows: [
        {
          label: 'Manage Coaches',
          description: 'Set up coaches and what they can access.',
          ...linkRow('/dashboard/manage-coaches'),
          visible: primaryAdmin || manageUsers,
        },
        {
          label: 'Blackout Dates',
          description: 'Set dates to prevent coaches from viewing athlete activity and exercise data.',
          ...linkRow('/dashboard/blackout-dates'),
          visible: primaryAdmin,
        },
      ],
    },
    {
      title: 'Exercises',
      rows: [
        {
          label: 'Manage Exercises',
          description: 'Manage exercises in your account and from imported programs.',
          ...linkRow('/dashboard/exercises', { fromManage: true }),
          visible: exercises,
        },
      ],
    },
    {
      title: 'Groups',
      rows: [
        {
          label: 'Manage Groups',
          description: 'Groups are used for reporting. You can assign your athletes to them here.',
          ...linkRow('/dashboard/manage-groups'),
          visible: hasGroupAccess,
        },
      ],
    },
    {
      title: 'Programs',
      rows: [
        {
          label: 'Manage Programs',
          description: 'Create workout to be completed at an athlete’s own pace regardless of dates.',
          ...linkRow('/dashboard/manage-programs'),
          visible: true,
        },
      ],
    },
  ].map((section) => ({
    ...section,
    rows: section.rows.filter((row) => row.visible !== false),
  })).filter((section) => section.rows.length > 0);

  return (
    <PageWrapper>
      {isMobile && (
        <MobileBackLink type='button' onClick={() => history.push('/dashboard')}>
          <Icon icon='left-arrow' size={12} color='#77A7FF' />
          Back to Home
        </MobileBackLink>
      )}
      <HeaderContainer>
        <Text styledTextProps={{
          fontSize: '24px', fontWeight: 900, color: '#1A1A1A',
        }}
        >
          Manage Your Account
        </Text>
      </HeaderContainer>

      <ContentWrapper>
        {SECTIONS.map(({ title, rows }) => (
          <Section key={title}>
            <Text styledTextProps={{
              fontSize: '16px', fontWeight: 700, color: '#1A1A1A', marginBottom: '8px',
            }}
            >
              {title}
            </Text>
            <SectionRows>
              {rows.map(({
                label, description, onClick, href,
              }) => (
                <Row key={label}>
                  <ActionButton
                    {...(href ? { as: 'a', href } : { type: 'button' })}
                    clickable={!!onClick}
                    onClick={onClick}
                  >
                    <Text styledTextProps={{ fontSize: '14px', fontWeight: 700, color: '#1A1A1A' }}>
                      {label}
                    </Text>
                  </ActionButton>
                  <Text styledTextProps={{ fontSize: '12px', color: '#1A1A1A' }}>
                    {description}
                  </Text>
                </Row>
              ))}
            </SectionRows>
          </Section>
        ))}
      </ContentWrapper>
    </PageWrapper>
  );
};

ManageHub.propTypes = {
  isMobile: PropTypes.bool,
};

ManageHub.defaultProps = {
  isMobile: false,
};

export default ManageHub;
